import { motion } from "framer-motion";
import { Shuffle } from "lucide-react";

const advisors = [
  { name: "Balanced Strategic Advisor", desc: "Weigh evidence, keep tradeoffs honest, stay curious." },
  { name: "CEO", desc: "Prioritise impact, execute, protect focus." },
  { name: "Startup Founder", desc: "Ship, learn, cut what isn't working." },
  { name: "Operator", desc: "Systems, checklists, boring execution wins." },
  { name: "Investor", desc: "Risk-adjusted returns and honest downside." },
  { name: "Career Coach", desc: "Move toward work that fits your values and market." },
  { name: "Hiring Manager", desc: "Read the market like a buyer of talent." },
  { name: "Negotiation Coach", desc: "Interests over positions. Walk-away power over anger." },
  { name: "Executive Coach", desc: "Behaviour change compounds; awareness is step one." },
  { name: "Financial Planner", desc: "Match money to life priorities, not the other way around." },
  { name: "Risk Analyst", desc: "Name the failure modes before the upside." },
  { name: "Long-Term Investor", desc: "Compounding rewards patience." },
  { name: "Trusted Friend", desc: "Be kind. Be honest. Don't tell them what they want to hear." },
  { name: "Wise Mentor", desc: "Time changes what looks urgent." },
  { name: "Future Self", desc: "What does the person you want to become do here?" },
  { name: "Devil's Advocate", desc: "The best defence against a bad decision is honest attack." },
  { name: "Socratic Questioner", desc: "Every belief deserves a why." },
  { name: "First Principles Thinker", desc: "Strip the situation to what's actually true." },
  { name: "Systems Thinker", desc: "Look for feedback loops, not single levers." },
  { name: "Pragmatist", desc: "Good enough today beats perfect never." },
];

const seat = (i) => `SEAT — ${String(i + 1).padStart(2, "0")}`;

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05 } },
};

const card = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

export default function Advisors() {
  return (
    <section
      data-testid="advisors-section"
      className="relative bg-[#0E0D0B] px-6 md:px-12 py-28 md:py-40 border-t border-white/[0.07]"
    >
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="mb-16 md:mb-24 grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
      >
        <div className="md:col-span-7">
          <p className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-[#C9B896]/80 mb-8">
            05 — The Board
          </p>
          <h2 className="font-serif-display font-light tracking-tighter leading-[0.95] text-5xl md:text-7xl text-white">
            Meet <span className="italic">the board.</span>
          </h2>
        </div>
        <p className="md:col-span-5 text-sm md:text-base leading-relaxed text-stone-400 max-w-md">
          Twenty advisors, each with their own counsel. Pick the mind your
          decision needs — or let Boardroom seat the right one for you.
        </p>
      </motion.div>

      <motion.div
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-60px" }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
      >
        {advisors.map((a, i) => (
          <motion.div
            key={a.name}
            variants={card}
            whileHover={{ y: -5 }}
            data-testid={`advisor-card-${seat(i).slice(-2)}`}
            className="group flex flex-col bg-[#171512] border border-white/[0.07] rounded-2xl p-5 hover:border-[#C9B896]/30 transition-colors duration-300"
          >
            <p className="font-mono-label text-[9px] tracking-[0.25em] text-[#C9B896]/80 mb-4">
              {seat(i)}
            </p>
            <h3 className="font-serif-display font-light tracking-tight text-xl text-white mb-1.5">
              {a.name}
            </h3>
            <p className="text-sm leading-relaxed text-stone-400">{a.desc}</p>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        data-testid="advisor-card-auto"
        className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-6 bg-[#C9B896] rounded-2xl p-7 md:p-9"
      >
        <div className="flex items-start gap-5">
          <Shuffle className="w-6 h-6 mt-1 shrink-0 text-[#0E0D0B]/70" strokeWidth={1.5} />
          <div>
            <h3 className="font-serif-display font-light tracking-tight text-3xl md:text-4xl text-[#0E0D0B] mb-2">
              Auto — pick for me
            </h3>
            <p className="text-sm md:text-base leading-relaxed text-[#0E0D0B]/75 max-w-lg">
              Not sure? Describe your situation and Boardroom matches the
              right advisor after you start.
            </p>
          </div>
        </div>
        <p className="font-mono-label text-[9px] tracking-[0.25em] uppercase text-[#0E0D0B]/60 shrink-0">
          SEAT — 21 · Always open
        </p>
      </motion.div>
    </section>
  );
}
